import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { t } from 'ttag';
import useBacNet from '@hooks/useBacNet';
import { useStoreState, useStoreActions } from '@models/RootStore';
import { BacnetDevice } from 'coolremote-sdk';

import { BACNET_LINES } from '../../../../constants/bacnet';
import {
  BaseDialog,
  Typography,
  Select,
} from '@coolremote/shared';
import useStyles from './styles';
import Results from './components/Results';
import Header from './components/Header';
import NoResults from './components/NoResults';
import DiscoveryProgress from './components/DiscoveryProgress';
import { smartStructureConvert } from '@utils/StructureConversionUtils';

/**
 * Dialog for adding discovered BACnet devices.
 * Switchable view showing discovering state or results table.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Close handler (called with true if devices were added, else false)
 * @param {string} props.siteId - The current site ID
 * @param {Array} props.newDevices - Devices discovered so far, owned by the parent so they
 *   survive this dialog closing and reopening.
 * @param {Function} props.onNewDevicesChange - Setter for props.newDevices (accepts a value or updater function)
 */
const AddBacnetDevicesDialog = ({ mode, onClose, siteId, newDevices, onNewDevicesChange }) => {
  const classes = useStyles();
  const {
    siteBacNetDevices,
    getSiteBacNetDevices,
    pollSiteBacNetDevices,
    discoverBacnetUnitsByDevice,
    updateMultipleBacNetDevices,
  } = useBacNet();

  const {bacnetDeviceId, bacnetLineId} = useStoreState((state) => state.selections.selections);
  const updateSelections = useStoreActions((a) => a.selections.updateSelections);
  const { addMessage } = useStoreActions((action) => action.errorMessage);


  // Load available CoolAutomation devices supporting BACnet and resolve names
  const allDevices = useStoreState((state) => state.devices.allDevices);
  const getDevicesTypesNames = useStoreState((state) => state.getDevicesTypesNames);

  const deviceOptions = useMemo(() => {
    if (!allDevices) return [];
    return Object.values(allDevices)
      .filter((device) => device.site == siteId && (device.type === 3 || device.itcVersion))
      .map((dev) => {
        const deviceTypeName = getDevicesTypesNames(dev.deviceType, dev.type);
        return {
          value: dev.id,
          label: `${deviceTypeName} (${dev.serial})`,
        };
      });
  }, [allDevices, siteId, getDevicesTypesNames]);

  const deviceSelectOptions = useMemo(() => ([
    { value: '', label: t`All devices`, name: t`All devices`, key: 'all' },
    ...deviceOptions,
  ]), [deviceOptions]);

  const lineSelectOptions = useMemo(() => ([
    { value: '', label: t`All lines`, name: t`All lines`, key: 'all' },
    ...BACNET_LINES,
  ]), []);

  // Selections
  const [selectedDeviceId, setSelectedDeviceId] = useState(bacnetDeviceId);
  const [selectedLine, setSelectedLine] = useState(bacnetLineId);

  // States
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const [isPolling, setIsPolling] = useState(false);
  // The discover request itself, before we know whether there is anything to wait for
  const [isStartingDiscovery, setIsStartingDiscovery] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Bumped on every discovery run, so the progress block remounts and restarts its animation
  const [discoveryRunId, setDiscoveryRunId] = useState(0);
  // Snapshot of what the last run actually scanned. Deliberately not bound to the live
  // selections, so changing a dropdown afterwards cannot reword messages about a run
  // that never covered it.
  const [discoveryScope, setDiscoveryScope] = useState(null);

  const pollTickRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  // True only while a poll run's results/errors are still worth acting on - false the moment
  // the run is stopped (dialog closed, filters changed, 60s cap hit), so a request that was
  // already in flight can't update state or show an error after nobody is watching anymore.
  const isActiveRef = useRef(false);
  // Device/line scopes already scanned in this dialog session, as the exact {deviceId, line}
  // selection scanned at the time ('' means "All" for either). Reselecting a deviceId/line
  // pair already covered by one of these - including a broader "All devices"/"All lines" scan
  // that necessarily scanned it too, per buildDiscoverDevices below - shouldn't trigger another
  // real discovery scan and progress bar; it's just a view filter over devices we already
  // found. Only a scope that isn't covered yet, or an explicit Refresh click, kicks off a scan.
  const scannedScopesRef = useRef([]);
  const isScopeCovered = (deviceId, line) =>
    scannedScopesRef.current.some((scope) =>
      (!scope.deviceId || scope.deviceId === deviceId) &&
      (!scope.line || scope.line === line)
    );

  // Filter devices: matches selected scope, isUserSelected !== true, recentlyDiscovered === true
  const filteredDevices = useMemo(() => {
    
    if(!Array.isArray(newDevices)){
      return [];
    }

    return newDevices.filter(
          (device) => 
            (!selectedDeviceId || device.device === selectedDeviceId) &&
            (!selectedLine || device.line == selectedLine) && 
            device.isUserSelected !== true &&
            device.isRecentlyDiscovered === true
        )
      
  }, [newDevices, selectedDeviceId, selectedLine]);


  // Merges the latest fetch into what we already know instead of replacing it, so a device
  // found under one device/line scope (or in an earlier dialog session) isn't lost the moment
  // a later fetch - scoped differently, or reporting it as no longer "recently discovered" -
  // comes back without it. isRecentlyDiscovered is sticky for the same reason: once a device
  // has shown up as newly discovered, it keeps showing until the user adds it.
  const mergeDevices = (previousDevices, incomingDevices) => {
    const byId = new Map(previousDevices.map((device) => [device.id, device]));
    incomingDevices.forEach((incomingDevice) => {
      const previousDevice = byId.get(incomingDevice.id);
      byId.set(incomingDevice.id, {
        ...previousDevice,
        ...incomingDevice,
        isRecentlyDiscovered: incomingDevice.isRecentlyDiscovered || previousDevice?.isRecentlyDiscovered || false,
      });
    });
    return Array.from(byId.values());
  };

  const updateNewDevices = (newDevicesObject) => {
    const units = smartStructureConvert(newDevicesObject).array;
    onNewDevicesChange((previousDevices) => mergeDevices(previousDevices, units));
  }

  // Polls once, then schedules the next tick relative to when THIS one was sent: if the
  // response came back within 5s, wait out the remainder of that window; if the backend was
  // already slower than 5s, fire the next request immediately instead of adding another wait
  // on top - keeps a steady ~5s cadence when healthy without piling up requests when it isn't.
  // Checks isActiveRef both before and after the fetch, so a run that was stopped while this
  // request was in flight neither updates state nor shows an error for it.
  const pollOnce = useCallback(async () => {
    if (!isActiveRef.current) {
      return;
    }

    const requestStartedAt = Date.now();

    try {
      const results = await pollSiteBacNetDevices(siteId, true);
      if (!isActiveRef.current) {
        return;
      }
      updateNewDevices(results);
    } catch (err) {
      if (isActiveRef.current) {
        addMessage({ message: err.message || t`Failed to fetch BACnet devices` });
      }
    }

    if (isActiveRef.current) {
      const elapsed = Date.now() - requestStartedAt;
      const nextDelay = Math.max(0, 5000 - elapsed);
      pollTickRef.current = setTimeout(pollOnce, nextDelay);
    }
  }, [siteId, pollSiteBacNetDevices, addMessage]);

  const startDiscoveryAndPolling = useCallback(async (skipDiscover = false) => {

    // Clear existing polling/timers
    isActiveRef.current = false;
    if (pollTickRef.current) clearTimeout(pollTickRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

    setErrorMsg('');
    setIsStartingDiscovery(true);

    const selectedDeviceSerial = allDevices?.[selectedDeviceId]?.serial;
    const hasDevice = Boolean(selectedDeviceId);
    const hasLine = Boolean(selectedLine);
    setDiscoveryScope({
      deviceSerial: selectedDeviceSerial,
      isSpecific: hasDevice && hasLine,
    });

    // Build the devices payload for the four device/line combinations the backend supports:
    // All+All -> undefined (send {}), All+line -> every known device paired with that line,
    // device+All -> that device alone (no line), device+line -> that exact pair.
    const buildDiscoverDevices = () => {
      if (hasDevice && hasLine) {
        return [{ serial: selectedDeviceSerial, line: selectedLine }];
      }
      if (hasDevice && !hasLine) {
        return [{ serial: selectedDeviceSerial }];
      }
      if (!hasDevice && hasLine) {
        return deviceOptions
          .map((opt) => allDevices[opt.value]?.serial)
          .filter(Boolean)
          .map((serial) => ({ serial, line: selectedLine }));
      }
      return undefined;
    };

    try {
      // Trigger discovery on the backend for the resolved device/line scope
      const devices = buildDiscoverDevices();
      const { hasOld, hasNew } = skipDiscover ? {hasNew: true, hasOld: true} : await BacnetDevice.discoverBacnetDevices({
        siteId,
        devices,
      });

     if(!hasOld && !hasNew){
      // Nothing to wait for - NoResults covers this state, no need for a second message
      setIsPolling(false);
      return;
     }

     if(!hasNew && hasOld){
      // Fetch immediately - as we already have all the data (only old units found)
      const results = await getSiteBacNetDevices(siteId, true, true);
      updateNewDevices(results);
      setIsPolling(false);
      return;
     }

      // Only now is there a real wait to visualise, so this is where the progress window opens
      setIsPolling(true);
      setDiscoveryRunId((prev) => prev + 1);
      isActiveRef.current = true;

      // First tick in 5 seconds; pollOnce reschedules itself after each one resolves.
      pollTickRef.current = setTimeout(pollOnce, 5000);

      // Stop polling after 60 seconds (1 minute)
      pollTimeoutRef.current = setTimeout(() => {
        stopPolling();
      }, 60000);

    } catch (err) {
      setErrorMsg(err.message || t`Failed to start discovery process`);
      setIsPolling(false);
    } finally {
      setIsStartingDiscovery(false);
    }
  }, [siteId, selectedDeviceId, selectedLine, discoverBacnetUnitsByDevice, getSiteBacNetDevices, pollOnce]);

  const stopPolling = () => {
    isActiveRef.current = false;
    setIsPolling(false);
    if (pollTickRef.current) {
      clearTimeout(pollTickRef.current);
      pollTickRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (isScopeCovered(selectedDeviceId, selectedLine)) {
      // A scan we already ran (this one or a broader one) covers this selection -
      // filteredDevices re-filters on its own from the deps below, no need to hit the backend.
      return;
    }
    scannedScopesRef.current.push({ deviceId: selectedDeviceId, line: selectedLine });
    startDiscoveryAndPolling();
  }, [selectedDeviceId, selectedLine]);

  useEffect(() => {
    if (mode === "discover") {
      scannedScopesRef.current.push({ deviceId: selectedDeviceId, line: selectedLine });
      startDiscoveryAndPolling(true);
    }
    return () => {
      // Cleanup on unmount
      isActiveRef.current = false;
      if (pollTickRef.current) clearTimeout(pollTickRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  // Handle individual checkbox change
  const handleToggleSelect = (deviceId) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  // Handle select all visible checkbox change
  const handleToggleSelectAll = (allVisibleIds) => {
    const areAllSelected = allVisibleIds.every((id) => selectedDeviceIds.has(id));

    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (areAllSelected) {
        // Uncheck all visible devices
        allVisibleIds.forEach((id) => next.delete(id));
      } else {
        // Check all visible devices
        allVisibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  // Handle save/add devices action
  const handleAddDevices = async () => {
    if (selectedDeviceIds.size === 0) return;

    setIsSaving(true);
    setErrorMsg('');
    stopPolling();

    try {
      // Call updateMultipleBacNetDevices for selected devices
      await updateMultipleBacNetDevices(
        Array.from(selectedDeviceIds).map((id) => ({
          id,
          data: { isUserSelected: true },
        }))
      );

      // Sync the top-level device/line filters to what was used to add these devices
      if (selectedDeviceId) {
        updateSelections({ type: 'bacnetDevice', data: selectedDeviceId });
      }
      if (selectedLine !== null && selectedLine !== undefined && selectedLine !== '') {
        updateSelections({ type: 'bacnetLine', data: selectedLine });
      }

      onClose(true, Array.from(selectedDeviceIds)); // Close dialog and request parent refresh
    } catch (err) {
      setErrorMsg(err.message || t`Failed to add some BACnet devices`);
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    stopPolling();
    onClose(false);
  };

  const handleSelectChange = (name) => (val = null) => {
    const action = name === 'device' ? setSelectedDeviceId : setSelectedLine;
    action(val);
  }

  const handleRefresh = () => {
    startDiscoveryAndPolling();
  };

  const renderContent = () => {
    const hasResults = filteredDevices?.length > 0;

    return (
      <>
        {/* Devices show up as they are discovered, so the bar and the table coexist */}
        {isPolling && <DiscoveryProgress key={discoveryRunId} />}

        {hasResults && (
          <Results
            devices={filteredDevices}
            selectedDeviceIds={selectedDeviceIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            allDevices={allDevices}
            isSaving={isSaving}
          />
        )}

        {!isPolling && !hasResults && <NoResults scope={discoveryScope} />}
      </>
    );
  };

  return (
    <BaseDialog
      size={'xlarge'}
      className={classes.addDialog}
      open={Boolean(mode)}
      onClose={handleCancel}
      title={t`Add BACnet Device`}
      onConfirm={handleAddDevices}
      onCancel={handleCancel}
      confirmLabel={`${t`+ Add Device${selectedDeviceIds.size === 1 ? '' : 's'}`} (${selectedDeviceIds.size})`}
      cancelLabel={t`Cancel`}
      confirmDisabled={selectedDeviceIds.size === 0 || isSaving}
      hideCloseButton={isSaving}
      containerClassName={classes.dialogContent}
    >
      {/* Dropdowns Row */}
      <div className={classes.selectorsRow}>
        <div className={classes.selectorField}>
          <Typography className={classes.selectorLabel}>{t`CoolAutomation Device`}</Typography>
          <Select
            fullWidth
            menuPortal
            value={selectedDeviceId || ''}
            onChange={handleSelectChange('device')}
            className={classes.selectField}
            placeholder={t`Select CoolAutomation Device...`}
            options={deviceSelectOptions}
          />
        </div>

        <div className={classes.selectorField}>
          <Typography className={classes.selectorLabel}>{t`Line`}</Typography>
          <Select
            fullWidth
            menuPortal
            value={selectedLine || ''}
            onChange={handleSelectChange('line')}
            className={classes.selectField}
            placeholder={t`Select line...`}
            options={lineSelectOptions}
          />
        </div>
      </div>

      {errorMsg && <Typography className={classes.errorText} color="error">{errorMsg}</Typography>}

      
      {!isPolling && <Header onRefresh={handleRefresh} isLoading={isStartingDiscovery} />}

      {/* Center Panel Content */}
      {renderContent()}
    </BaseDialog>
  );
};

export default AddBacnetDevicesDialog;
